import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentHead } from '@/shared/hooks/useDocumentHead';
import { trackRumAction } from '@/shared/observability/rum';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import CareConciergContactForm from '@/features/provider-directory/components/CareConciergContactForm';

/**
 * Feature 104: public, login-optional shareable URL for the FP-specific Care
 * Concierge intake (/care-concierge/advisors). Form only — no chooser, no
 * consultation tiers (those are care-seeker-facing). Signed-in FAs get their
 * existing prefill; guests submit through the guest path (reCAPTCHA guest
 * session), which the backend already validates for the FA workflow.
 */
export const AdvisorConciergePage = () => {
  const { user } = useAuth();

  useDocumentHead({
    title: 'Care Concierge for Financial Professionals | bQuest',
    description:
      'Request Care Concierge support for a client — for financial professionals. No login required.',
  });

  useEffect(() => {
    trackRumAction('concierge_fp_page_view', { authenticated: Boolean(user) });
  }, [user]);

  const chipLabel = user
    ? `Signed in as ${[user.firstName, user.lastName].filter(Boolean).join(' ')}`
    : 'Guest — no login required';

  return (
    <div className="bg-background min-h-screen">
      <div className="container max-w-3xl px-4 md:px-6 py-8 md:py-12">
        <header className="mb-6">
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
              user ? 'bg-[#026857]/10 text-[#026857]' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {chipLabel}
          </span>
          <h1 className="mt-3 text-3xl font-recoleta font-semibold text-[#111827]">
            For financial professionals requesting Care Concierge support for a client
          </h1>
          <Link
            to="/care-concierge"
            className="mt-2 inline-block text-sm font-semibold text-[#026857] underline-offset-2 hover:underline"
          >
            Looking for care for yourself or a loved one? Go here instead.
          </Link>
        </header>

        <CareConciergContactForm
          pageOrigin="fp-shareable-page"
          forceFinancialAdvisorWorkflow
          allowGuestSubmission
        />
      </div>
    </div>
  );
};
